import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, orderedAccounts } from '../../store';
import { api } from '../../utils/api';
import type { AutoCcSettings } from '../../types';
import { autoCcEntry } from '../../hooks/useAutoCc';
import { fmtLabelStamp } from '../../utils/emailLabels';
import { ACCOUNT_COLORS, accentVars, tint } from '../../utils/accountColors';

type Field = 'cc' | 'bcc' | 'skip';
type Lists = Record<Field, string[]>;
const FIELDS: Field[] = ['cc', 'bcc', 'skip'];
const EMPTY: Lists = { cc: [], bcc: [], skip: [] };
const sameLists = (a: Lists, b: Lists) => FIELDS.every(f => a[f].length === b[f].length && a[f].every((v, i) => v === b[f][i]));

const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 };
const hintStyle: React.CSSProperties = { fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, lineHeight: 1.4 };

// One list as removable chips. Enter, a comma or leaving the box adds what is typed; an entry that
// isn't a valid address stays in the box with the reason under it (and Save waits for it).
function AddressList({ values, domains, placeholder, onChange, onBad }: {
  values: string[]; domains: boolean; placeholder: string;
  onChange: (v: string[]) => void; onBad: (bad: boolean) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [bad, setBad] = useState('');
  const commit = () => {
    const add: string[] = [], wrong: string[] = [];
    for (const part of text.split(/[,;\s]+/).filter(Boolean)) {
      const e = autoCcEntry(part, domains);
      if (!e) wrong.push(part);
      else if (!values.includes(e) && !add.includes(e)) add.push(e);
    }
    if (add.length) onChange([...values, ...add]);
    setText(wrong.join(', '));
    setBad(wrong.length ? t('eml_autocc_invalid', { value: wrong.join(', ') }) : '');
    onBad(wrong.length > 0);
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, minHeight: 36, padding: '4px 8px', borderRadius: 'var(--radius-md)', border: `1px solid ${bad ? '#E24B4A' : 'var(--border-strong)'}`, background: 'var(--c-gray-50)' }}>
        {values.map(v => (
          <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '2px 7px', fontSize: 12, color: 'var(--text-primary)' }}>
            {v}
            <button onClick={() => onChange(values.filter(x => x !== v))} title={t('eml_autocc_remove')} style={{ border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <i className="ti ti-x" style={{ fontSize: 10 }} aria-hidden />
            </button>
          </span>
        ))}
        <input value={text} placeholder={values.length ? '' : placeholder}
          onChange={e => { setText(e.target.value); if (bad) { setBad(''); onBad(false); } }}
          onBlur={() => { if (text.trim()) commit(); }}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ',' || e.key === ';') && text.trim()) { e.preventDefault(); commit(); }
            else if (e.key === 'Backspace' && !text && values.length) onChange(values.slice(0, -1));
          }}
          style={{ flex: 1, minWidth: 160, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)', padding: '3px 0' }} />
      </div>
      {bad && <div style={{ fontSize: 11, color: '#E24B4A', marginTop: 4 }}>{bad}</div>}
    </div>
  );
}

// The signed-in user's own Auto CC / BCC (Bugify f41022ce, Calem) — opened from the PandaAI Email
// sidebar. Each mailbox has its own three lists (Calem, opening #3): the picker at the top chooses the
// one on screen, in its own colour, and Save writes every mailbox changed here. The composers apply
// the From mailbox's lists (hooks/useAutoCc); nothing here changes emails already written.
export default function AutoCcModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { showToast, activeAccountId } = useStore();
  const accounts = useStore(orderedAccounts);   // the sidebar's order
  const [accountId, setAccountId] = useState(() => (accounts.find(a => a.id === activeAccountId) || accounts[0])?.id || '');
  // Per mailbox, once loaded: what is stored, and what is on screen. Every mailbox picked here keeps
  // its own, so switching back and forth loses nothing.
  const [stored, setStored] = useState<Record<string, AutoCcSettings>>({});
  const [lists, setLists] = useState<Record<string, Lists>>({});
  const [bad, setBad] = useState<Field[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setBad([]);   // the lists below remount for this mailbox, with nothing half-typed in them
    if (!accountId || stored[accountId]) return;
    let alive = true;
    api.getAutoCc(accountId)
      .then(s => {
        if (!alive) return;
        setStored(p => ({ ...p, [accountId]: s }));
        setLists(p => ({ ...p, [accountId]: { cc: s.cc, bcc: s.bcc, skip: s.skip } }));
      })
      .catch(() => { if (alive) setErr(t('eml_autocc_load_failed')); });
    return () => { alive = false; };
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const acct = accounts.find(a => a.id === accountId);
  const cur = lists[accountId];   // undefined until loaded — the lists stay greyed until then
  const savedAt = stored[accountId]?.updated_at;
  // Save writes only these — mailboxes whose lists have loaded and differ — so one that never loaded
  // can't be wiped.
  const changed = Object.keys(lists).filter(id => stored[id] && !sameLists(lists[id], stored[id]));
  const locked = busy || bad.length > 0;   // an entry still to be fixed keeps its mailbox on screen
  const canSave = !locked && Object.keys(stored).length > 0;
  const pick = (id: string) => { if (id === accountId || locked) return; setErr(''); setAccountId(id); };

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setErr('');
    for (const id of changed) {
      try {
        const s = await api.saveAutoCc(id, lists[id]);
        setStored(p => ({ ...p, [id]: s }));
        setLists(p => ({ ...p, [id]: { cc: s.cc, bcc: s.bcc, skip: s.skip } }));
      } catch (e: any) {
        // Stop at the mailbox that failed and put it on screen; the ones before it are saved.
        setAccountId(id);
        setErr(`${accounts.find(a => a.id === id)?.email || ''}: ${e?.message || String(e)}`);
        setBusy(false);
        return;
      }
    }
    showToast(t('eml_autocc_saved'));
    onClose();
  };

  const section = (field: Field, icon: string, title: string, hint: string, placeholder: string) => (
    <div>
      <label style={labelStyle}><i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden />{title}</label>
      <AddressList values={(cur || EMPTY)[field]} domains={field === 'skip'} placeholder={placeholder}
        onChange={v => setLists(p => (p[accountId] ? { ...p, [accountId]: { ...p[accountId], [field]: v } } : p))}
        onBad={b => setBad(p => (b ? (p.includes(field) ? p : [...p, field]) : p.filter(f => f !== field)))} />
      <div style={hintStyle}>{hint}</div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Wears the chosen mailbox's colour, like the email module wears the open one's */}
      <div style={{ ...accentVars(acct?.color), background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="ti ti-mail-forward" style={{ fontSize: 16, color: 'var(--accent)' }} aria-hidden />
              {t('eml_autocc')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t('eml_autocc_sub')}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        {/* Mailbox — whose three lists are on screen. Each button wears its mailbox's colour, as in the
            sidebar; a dot marks one changed here but not saved yet. */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <label style={labelStyle}><i className="ti ti-mailbox" style={{ fontSize: 13 }} aria-hidden />{t('eml_autocc_mailbox')}</label>
          {accounts.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 92, overflowY: 'auto' }}>
              {accounts.map(a => {
                const c = ACCOUNT_COLORS[a.color] || ACCOUNT_COLORS.teal;
                const on = a.id === accountId;
                const initials = a.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <button key={a.id} onClick={() => pick(a.id)} disabled={!on && locked} aria-pressed={on} title={`${a.name} <${a.email}>`}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: '100%', padding: '3px 10px 3px 3px', borderRadius: 14, border: `1px solid ${on ? c.accent : tint(c.accent, 0.35)}`, background: on ? c.accent : c.bg, color: on ? '#fff' : c.text, fontSize: 12, fontWeight: on ? 600 : 500, cursor: on ? 'default' : locked ? 'not-allowed' : 'pointer', opacity: !on && locked ? 0.6 : 1 }}>
                    {/* The initials trimmed to their cap height, so the flex box centres the capitals themselves (Bugify #836d4443, 2nd opening) */}
                    <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 600, background: on ? c.bg : c.accent, color: on ? c.accent : c.bg }}>
                      <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{initials}</span>
                    </span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.email}</span>
                    {changed.includes(a.id) && <span title={t('eml_autocc_unsaved')} aria-label={t('eml_autocc_unsaved')} style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: on ? '#fff' : c.accent }} />}
                  </button>
                );
              })}
            </div>
          ) : <div style={hintStyle}>{t('eml_autocc_no_mailbox')}</div>}
        </div>

        {/* How it works */}
        <div style={{ padding: '10px 20px', background: 'var(--accent-bg)', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--accent-text)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <i className="ti ti-info-circle" style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
          {t('eml_autocc_note')}
        </div>

        {/* The chosen mailbox's lists — keyed by it, so a half-typed entry never carries over to another
            mailbox; greyed until they have loaded. */}
        <div key={accountId} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: cur ? 1 : 0.5, pointerEvents: cur ? 'auto' : 'none' }}>
            {section('cc', 'ti-users', t('eml_autocc_cc'), t('eml_autocc_cc_hint'), t('eml_autocc_placeholder'))}
            {section('bcc', 'ti-eye-off', t('eml_autocc_bcc'), t('eml_autocc_bcc_hint'), t('eml_autocc_placeholder'))}
            {section('skip', 'ti-ban', t('eml_autocc_skip'), t('eml_autocc_skip_hint'), t('eml_autocc_skip_placeholder'))}
          </div>
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--text-tertiary)' }}>
            {savedAt ? t('eml_autocc_last_saved', { time: fmtLabelStamp(savedAt) }) : ''}
          </span>
          <button onClick={onClose} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={save} disabled={!canSave} style={{ padding: '7px 18px', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.6 }}>{t('save')}</button>
        </div>
      </div>
    </div>
  );
}
